import { clarity } from 'react-microsoft-clarity';

interface CustomWindow extends Window {
  clarity: (...args: any[]) => void;
}

/**
 * Classe para abstração das funcionalidades do Microsoft Clarity.
 *
 * Esta classe fornece uma interface simplificada para interagir com o Microsoft Clarity,
 * encapsulando chamadas para a biblioteca `react-microsoft-clarity`.
 *
 * @class Clarity
 */
export class Clarity {
  /**
   * Executa um comando do Clarity com os argumentos fornecidos.
   *
   * @private
   * @param {...any[]} args - Os argumentos a serem passados para o Clarity.
   * @throws {Error} Se o Clarity não estiver inicializado.
   */
  private static clarity(...args: any[]) {
    const params = Array.prototype.slice.call(args);

    if (!(window as unknown as CustomWindow).clarity) {
      throw new Error('Clarity is not initialized');
    }

    (window as unknown as CustomWindow).clarity.apply(undefined, params);
  }

  /**
   * Inicializa o rastreamento do Clarity.
   *
   * @param {string} id - Este é o ID que informa ao Clarity quais configurações do site devem ser carregadas e onde os dados coletados devem ser armazenados.
   */
  static init(id: string): void {
    clarity.init(id);
  }

  /**
   * Verifica se o Clarity foi inicializado.
   *
   * @returns {boolean} - Retorna true se o Clarity foi inicializado, caso contrário, false.
   */
  static hasStarted(): boolean {
    return clarity.hasStarted();
  }

  /**
   * Identifica o usuário.
   *
   * @param {string} userId - ID único do usuário.
   * @param {Record<string, any>} properties - Propriedades adicionais que descrevem o usuário.
   */
  static identify(userId: string, properties: Record<string, any>): void {
    clarity.identify(userId, properties);
  }

  /**
   * Consentimento de cookies.
   *
   * Solicita o consentimento do usuário para o uso de cookies.
   */
  static consent(): void {
    clarity.consent();
  }

  /**
   * Configura tags personalizadas.
   *
   * @param {string} key - O nome da tag que identifica o tipo de informação que você está enviando.
   * @param {string} value - O valor associado à tag.
   */
  static setTag(key: string, value: string): void {
    clarity.setTag(key, value);
  }

  /**
   * Atualiza a sessão.
   *
   * @param {string} reason - O motivo da atualização da sessão.
   */
  static upgrade(reason: string): void {
    clarity.upgrade(reason);
  }

  /**
   * Define uma chave e valor personalizados para o Clarity.
   *
   * @param {string} key - A chave que será associada ao valor.
   * @param {string} value - O valor associado à chave.
   */
  static set(key: string, value: string) {
    this.clarity('set', key, value);
  }
}
